import { generateQuickscanReport } from '@wtta/scoring'
import { useMemo } from 'react'

import { ErrorState } from './components/ErrorState'
import { EvidenceSection } from './components/EvidenceSection'
import { ExecutiveSummarySection } from './components/ExecutiveSummarySection'
import { PlanningSection } from './components/PlanningSection'
import { RecommendationsSection } from './components/RecommendationsSection'
import { ReportHeader } from './components/ReportHeader'
import { RoadmapSection } from './components/RoadmapSection'
import { ScoreSection } from './components/ScoreSection'
import { SystemImpactSection } from './components/SystemImpactSection'
import { WhitepaperSections } from './components/WhitepaperSections'
import { decodeUnsignedToken } from './lib/token'

export interface AppProps {
  /** Override hooks for tests; in production we read from window.location. */
  token?: string
  generatedAt?: Date
  quickscanRedirectUrl?: string
  /** Destination of the "Plan een gesprek" CTA in the system-impact section. */
  contactUrl?: string
}

const DEFAULT_QUICKSCAN_URL = 'https://wtta.nl/quickscan'
const DEFAULT_CONTACT_URL = 'https://calendar.app.google/g11G4j5towZYgDm47'

export function App({
  token: tokenProp,
  generatedAt = new Date(),
  quickscanRedirectUrl = DEFAULT_QUICKSCAN_URL,
  contactUrl = DEFAULT_CONTACT_URL,
}: AppProps) {
  const token = tokenProp ?? readTokenFromLocation()

  const decoded = useMemo(() => (token ? decodeUnsignedToken(token) : null), [token])

  if (!token) {
    return (
      <ErrorState
        title="Geen rapport-token gevonden"
        description="De link die je volgde mist een geldig token. Vraag de quickscan opnieuw aan om een nieuwe rapport-link te ontvangen."
        ctaUrl={quickscanRedirectUrl}
      />
    )
  }

  if (!decoded) {
    return (
      <ErrorState
        title="Deze rapport-link werkt niet meer"
        description="Het token is ongeldig of verlopen (rapporten zijn 30 dagen geldig). Doe de quickscan opnieuw om een nieuw rapport te ontvangen."
        ctaUrl={quickscanRedirectUrl}
      />
    )
  }

  const report = generateQuickscanReport(decoded.ct, decoded.a, { now: generatedAt })

  return (
    <main className="report" data-testid="report">
      <ReportHeader
        result={report.result}
        companyType={decoded.ct}
        generatedAt={generatedAt}
      />
      <ExecutiveSummarySection summary={report.executiveSummary} />
      <ScoreSection result={report.result} />
      <WhitepaperSections sections={report.whitepaperSections} />
      <RoadmapSection milestones={report.roadmap} now={generatedAt} />
      <EvidenceSection tracks={report.evidenceTracks} />
      <RecommendationsSection
        recommendations={report.recommendations}
        totalCostMin={report.totalEstimatedCostMin}
        totalCostMax={report.totalEstimatedCostMax}
        totalEffortDays={report.totalEffortDays}
      />
      <PlanningSection planning={report.planning} />
      <SystemImpactSection
        companyType={decoded.ct}
        contactUrl={contactUrl}
      />

      <footer className="report-footer">
        <p>
          Dit rapport is automatisch gegenereerd op basis van jouw quickscan-antwoorden.
        </p>
        <button
          type="button"
          className="report-footer__print"
          onClick={() => window.print()}
        >
          Rapport opslaan als PDF (print, dan opslaan als PDF)
        </button>
      </footer>
    </main>
  )
}

function readTokenFromLocation(): string | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  return params.get('token')
}
